/**
 * AskUserDialog — inline question dialog rendered when a tool call's
 * output has artifact_type="ask_user".
 *
 * Layout:
 *   - Question text on its own line
 *   - Options listed vertically, each prefixed with a numbered index
 *     (1, 2, 3, …) and a radio button (single-select) or checkbox
 *     (multi-select)
 *   - Optional freeform text input below the options
 *
 * Behaviour:
 *   - Single-select (default): clicking a radio submits immediately,
 *     the picked option's `value` is sent as the next user message.
 *   - Multi-select: user toggles checkboxes, then clicks Send. The
 *     submitted message is a comma-joined list of the selected
 *     option `value`s.
 *   - Freeform input: submits whatever the user typed.
 *   - Typed inputs (`payload.input`): a `number` field (with unit and
 *     min/max) or a `topology` s/p picker. These exist so a pack
 *     requirement arrives as an exact value — the deterministic pack
 *     math downstream is only as good as the number it is handed, and
 *     until 2026-08-04 every voltage/power/range answer was free prose
 *     the model had to interpret. The generic freeform text box is
 *     hidden for these, because it is the very ambiguity being removed;
 *     the main chat input is still there for "I don't know".
 *   - A form (`payload.fields`): one typed control per field. A 'choice'
 *     field carrying `options` is a <select> pre-set on its `default` (else
 *     the first option); it answers with the option's `value` and echoes
 *     its `label`. Number/text fields are plain inputs.
 *   - After ANY submission the dialog locks (controls disabled) so
 *     the same question can't be answered twice. State lives in
 *     chatStore so the lock survives re-renders during streaming.
 *   - Another host (the Ion Next admin page) passes `onAnswer` and
 *     `answered`: the answer goes to `onAnswer(text)` (the picked option's
 *     label, or the typed text) instead of the customer chat, and
 *     `answered` replaces the chatStore lock, which is
 *     then neither read nor written. `disabled` shows the dialog with its
 *     controls off (a read-only conversation). Without these props the
 *     customer behaviour is unchanged.
 *
 * The AI gets the user's reply as a normal next user turn —
 * Anthropic's tool protocol can't pause for human input mid-tool, so
 * we treat the tool call as "emit dialog, return immediately".
 */

import { useId, useState } from 'preact/hooks'
import { sendChatMessage } from '../../hooks/useChatStream.js'
import {
  buildFormAnswer,
  fieldSelectOptions,
  initialFieldValue,
  isFormComplete,
} from './askUserFormAnswer.js'
import {
  markAskUserAnswered,
  isAskUserAnswered,
} from '../../state/chatStore.js'

/**
 * @param {object} props
 * @param {string} props.toolCallId — stable key in the answered-set
 * @param {object} props.payload — { question, options, allow_freeform, multi_select, input }
 * @param {object} [props.contextHint] — same context the chat input uses
 * @param {(text: string) => void} [props.onAnswer] — the host sends the answer
 *   (instead of the customer chat's sendChatMessage + chatStore lock)
 * @param {boolean|string} [props.answered] — with `onAnswer`: the question was
 *   answered already (a string is shown as the answer)
 * @param {boolean} [props.disabled] — controls off without collapsing (read-only)
 */
export function AskUserDialog({ toolCallId, payload, contextHint, onAnswer, answered, disabled = false }) {
  const groupId = useId()
  const hosted = typeof onAnswer === 'function'
  const alreadyAnswered = hosted ? !!answered : isAskUserAnswered(toolCallId)
  const multi = !!payload?.multi_select
  const [singlePick, setSinglePick] = useState(null) // index of picked radio
  const [multiPicks, setMultiPicks] = useState(() => new Set()) // indexes of checked
  const [freeform, setFreeform] = useState('')
  const [numberValue, setNumberValue] = useState('')
  const [seriesValue, setSeriesValue] = useState('')
  const [parallelValue, setParallelValue] = useState('')
  const [submittedValue, setSubmittedValue] = useState(null)
  // A multi-field FORM (`payload.fields`) — the requirement phase asks for the
  // whole pack specification at once instead of one size per turn. Pre-filled
  // from the flow's defaults, so a customer who accepts the reference numbers
  // confirms in one click. Keyed by field slug. A 'choice' field with options
  // is a dropdown and starts on its default (else its first option) — see
  // askUserFormAnswer.js for the rule.
  const formFields = Array.isArray(payload?.fields) ? payload.fields : null
  const [formValues, setFormValues] = useState(() =>
    Object.fromEntries(
      (Array.isArray(payload?.fields) ? payload.fields : []).map((f) => [
        f.slug,
        initialFieldValue(f),
      ])
    )
  )

  if (!payload || !payload.question) return null

  const locked = alreadyAnswered || submittedValue !== null
  // Controls off: answered, or shown read-only by the host.
  const blocked = locked || !!disabled
  const options = payload.options || []
  // An unknown widget type falls back to the option list rather than rendering
  // nothing — a dialog with no way to answer would dead-end the flow.
  const inputSpec = payload.input || null
  const inputKind =
    // A form owns the whole dialog — no single widget, no option list, no
    // freeform box beside it. The backend rejects a payload carrying both, so
    // this only guards a hand-built one.
    formFields
      ? 'form'
      : inputSpec && (inputSpec.type === 'number' || inputSpec.type === 'topology')
        ? inputSpec.type
        : 'choice'
  // The typed field IS the freeform path for a number/topology question.
  const allowFreeform = payload.allow_freeform !== false && inputKind === 'choice'

  const send = (text, display) => {
    if (blocked) return
    const clean = String(text || '').trim()
    if (!clean) return
    // Show the option LABEL in the collapsed echo (it carries the unit, e.g.
    // "18 V" / "1200 W"), while still sending the bare VALUE to the backend.
    // Falls back to the sent text for freeform answers (no separate label).
    const shown = display != null ? String(display).trim() : clean
    setSubmittedValue(shown || clean)
    if (hosted) {
      // The host sends it as an ordinary user message (its own bubble): the
      // picked option's label, or the typed text.
      onAnswer(shown || clean)
      return
    }
    markAskUserAnswered(toolCallId)
    // askUserAnswer:true → the answer is sent to the backend but NOT rendered as
    // a separate user bubble; the collapsed card below shows the pick instead.
    sendChatMessage(clean, { contextHint, askUserAnswer: true })
  }

  const handleRadioPick = (idx) => {
    if (blocked) return
    setSinglePick(idx)
    const opt = options[idx]
    if (!opt) return
    // Single-select: clicking the radio is the answer — submit
    // immediately, no extra step. Echo the label (with unit), send the value.
    send(opt.value || opt.label, opt.label)
  }

  const handleCheckboxToggle = (idx) => {
    if (blocked) return
    setMultiPicks((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const handleMultiSubmit = () => {
    if (blocked || multiPicks.size === 0) return
    const pickedOpts = Array.from(multiPicks)
      .sort((a, b) => a - b)
      .map((i) => options[i])
      .filter(Boolean)
    const values = pickedOpts.map((o) => o.value || o.label).filter(Boolean)
    const labels = pickedOpts.map((o) => o.label || o.value).filter(Boolean)
    send(values.join(', '), labels.join(', '))
  }

  const handleFreeformSubmit = (e) => {
    e.preventDefault()
    send(freeform)
  }

  // --- The form ------------------------------------------------------------
  // Which fields must be filled, and what a blank means on the wire, both live
  // in askUserFormAnswer.js — see there for why an `optional` field exists.
  const formComplete = !!formFields && isFormComplete(formFields, formValues)

  const handleFormSubmit = (e) => {
    e.preventDefault()
    if (blocked || !formComplete) return
    const { wire, shown } = buildFormAnswer(formFields, formValues)
    send(wire, shown)
  }

  // --- Typed inputs ---------------------------------------------------------
  // Bounds are only honoured when the backend actually sent numbers; a missing
  // bound must not silently reject every value.
  const unit = inputSpec?.unit || ''
  const numMin = Number.isFinite(inputSpec?.min) ? inputSpec.min : null
  const numMax = Number.isFinite(inputSpec?.max) ? inputSpec.max : null
  const parsedNumber = numberValue.trim() === '' ? NaN : Number(numberValue)
  const numberValid =
    Number.isFinite(parsedNumber) &&
    (numMin === null || parsedNumber >= numMin) &&
    (numMax === null || parsedNumber <= numMax)

  const handleNumberSubmit = (e) => {
    e.preventDefault()
    if (!numberValid) return
    // Same convention as the radio path: send the bare value, echo it with the
    // unit so the collapsed card reads "✓ 48 V".
    const bare = String(parsedNumber)
    send(bare, unit ? `${bare} ${unit}` : bare)
  }

  // parseInt would accept "13abc"; Number + isInteger does not.
  const seriesCount = seriesValue.trim() === '' ? NaN : Number(seriesValue)
  const parallelCount = parallelValue.trim() === '' ? NaN : Number(parallelValue)
  const topologyValid =
    Number.isInteger(seriesCount) &&
    seriesCount >= 1 &&
    Number.isInteger(parallelCount) &&
    parallelCount >= 1

  const handleTopologySubmit = (e) => {
    e.preventDefault()
    if (!topologyValid) return
    // Canonical, self-describing form so the backend never has to guess which
    // number was the series count.
    send(`${seriesCount}s${parallelCount}p`)
  }

  // Once answered, COLLAPSE the card to the question + a compact "✓ <pick>" —
  // no separate user bubble (the answer was sent with askUserAnswer:true). This
  // removes the "doubled" look of the full dialog + an echoed user message.
  if (locked) {
    return (
      <div class="ion-ask ion-ask--answered" role="group" aria-label="Answered question">
        <p class="ion-ask__question ion-ask__question--answered">{payload.question}</p>
        <p class="ion-ask__sent" aria-live="polite">
          ✓ {submittedValue || (hosted && typeof answered === 'string' && answered) || 'Answered'}
        </p>
      </div>
    )
  }

  return (
    <div
      class={`ion-ask${blocked ? ' ion-ask--locked' : ''}`}
      role="group"
      aria-label="Question"
    >
      <p class="ion-ask__question">{payload.question}</p>

      {/* The requirement form. Rendered INSTEAD of the option list and the
          single typed field — the backend rejects a payload that carries both,
          so there is nothing to fall through to. */}
      {formFields && (
        <form class="ion-ask__form" onSubmit={handleFormSubmit}>
          {formFields.map((f) => {
            const fieldId = `${groupId}-f-${f.slug}`
            const spec = f.input || {}
            // A 'choice' field with >= 2 options is a dropdown; a 'choice' field
            // without options (older payloads) stays a text box.
            const selectOptions = fieldSelectOptions(f)
            const min = Number.isFinite(spec.min) ? spec.min : null
            const max = Number.isFinite(spec.max) ? spec.max : null
            const raw = String(formValues[f.slug] ?? '')
            const parsed = raw.trim() === '' ? NaN : Number(raw)
            const outOfRange =
              spec.type === 'number' &&
              Number.isFinite(parsed) &&
              ((min !== null && parsed < min) || (max !== null && parsed > max))
            const setValue = (e) =>
              setFormValues((prev) => ({
                ...prev,
                [f.slug]: e.currentTarget.value,
              }))
            return (
              <div key={f.slug} class="ion-ask__form-row">
                <label class="ion-ask__form-label" htmlFor={fieldId}>
                  {f.label}
                </label>
                <span class="ion-ask__form-control">
                  {selectOptions ? (
                    <select
                      id={fieldId}
                      class="ion-ask__typed-input"
                      value={raw}
                      disabled={blocked}
                      aria-label={f.label}
                      onChange={setValue}
                    >
                      {selectOptions.map((opt) => (
                        <option key={String(opt.value)} value={String(opt.value)}>
                          {opt.label || opt.value}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={fieldId}
                      type={spec.type === 'number' ? 'number' : 'text'}
                      inputMode={spec.type === 'number' ? 'decimal' : undefined}
                      class={`ion-ask__typed-input${outOfRange ? ' is-invalid' : ''}`}
                      min={min ?? undefined}
                      max={max ?? undefined}
                      step="any"
                      value={raw}
                      disabled={blocked}
                      aria-label={f.label}
                      onInput={setValue}
                    />
                  )}
                  {spec.unit && <span class="ion-ask__typed-unit">{spec.unit}</span>}
                </span>
                {/* The range is stated, not just enforced — a rejected value
                    with no visible bound is a dead end. "optional" is marked
                    here rather than left to the label's wording, so whether a
                    blank is accepted is told by the same thing that enforces
                    it.

                    ALWAYS RENDERED, even when empty. `.ion-ask__form-row` is
                    `display: contents`, so each row hands its children straight
                    to the form's 3-column grid. Omitting this span made that row
                    contribute two cells instead of three and shifted every
                    following field one column — labels landed on inputs and the
                    bare ranges stacked in the label column. A text field (no
                    min/max, not optional) is the trigger, which is why the form
                    only broke once the pre-selection fields were added. */}
                <span class="ion-ask__form-hint">
                  {[
                    min !== null && max !== null
                      ? `${min}–${max}`
                      : min !== null
                        ? `≥ ${min}`
                        : max !== null
                          ? `≤ ${max}`
                          : null,
                    f.optional ? 'optional' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            )
          })}
          <button
            type="submit"
            class="ion-ask__typed-submit"
            disabled={blocked || !formComplete}
          >
            Confirm
          </button>
        </form>
      )}

      {!formFields && options.length > 0 && (
      <ul class="ion-ask__options" role={multi ? 'group' : 'radiogroup'}>
        {options.map((opt, i) => {
          const optId = `${groupId}-opt-${i}`
          const checked = multi ? multiPicks.has(i) : singlePick === i
          return (
            <li key={i} class="ion-ask__option-row">
              <label class="ion-ask__option-label" htmlFor={optId}>
                <span class="ion-ask__option-index" aria-hidden="true">
                  {i + 1}.
                </span>
                <input
                  id={optId}
                  type={multi ? 'checkbox' : 'radio'}
                  name={multi ? `${groupId}-${i}` : groupId}
                  class="ion-ask__option-input"
                  checked={checked}
                  disabled={blocked}
                  onChange={() =>
                    multi ? handleCheckboxToggle(i) : handleRadioPick(i)
                  }
                />
                <span class="ion-ask__option-text">{opt.label}</span>
              </label>
            </li>
          )
        })}
      </ul>
      )}

      {inputKind === 'number' && (
        <form class="ion-ask__typed" onSubmit={handleNumberSubmit}>
          <div class="ion-ask__typed-field">
            <input
              type="number"
              inputMode="decimal"
              step="any"
              class="ion-ask__typed-input"
              aria-label={payload.question}
              placeholder={
                numMin !== null && numMax !== null ? `${numMin}–${numMax}` : 'Value'
              }
              min={numMin !== null ? numMin : undefined}
              max={numMax !== null ? numMax : undefined}
              value={numberValue}
              onInput={(e) => setNumberValue(e.target.value)}
              disabled={blocked}
            />
            {unit && (
              <span class="ion-ask__typed-unit" aria-hidden="true">
                {unit}
              </span>
            )}
          </div>
          <button
            type="submit"
            class="ion-ask__freeform-send"
            disabled={blocked || !numberValid}
            aria-label="Send value"
          >
            ↑
          </button>
        </form>
      )}

      {inputKind === 'topology' && (
        <form class="ion-ask__typed" onSubmit={handleTopologySubmit}>
          <div class="ion-ask__typed-field">
            <input
              type="number"
              inputMode="numeric"
              step="1"
              min="1"
              class="ion-ask__typed-input"
              aria-label="Cells in series"
              placeholder="s"
              value={seriesValue}
              onInput={(e) => setSeriesValue(e.target.value)}
              disabled={blocked}
            />
            <span class="ion-ask__typed-unit" aria-hidden="true">
              s
            </span>
          </div>
          <div class="ion-ask__typed-field">
            <input
              type="number"
              inputMode="numeric"
              step="1"
              min="1"
              class="ion-ask__typed-input"
              aria-label="Cells in parallel"
              placeholder="p"
              value={parallelValue}
              onInput={(e) => setParallelValue(e.target.value)}
              disabled={blocked}
            />
            <span class="ion-ask__typed-unit" aria-hidden="true">
              p
            </span>
          </div>
          <button
            type="submit"
            class="ion-ask__freeform-send"
            disabled={blocked || !topologyValid}
            aria-label="Send pack topology"
          >
            ↑
          </button>
        </form>
      )}

      {multi && !blocked && (
        <button
          type="button"
          class="ion-ask__multi-send"
          disabled={multiPicks.size === 0}
          onClick={handleMultiSubmit}
        >
          Send {multiPicks.size > 0 ? `(${multiPicks.size})` : ''}
        </button>
      )}

      {allowFreeform && (
        <form class="ion-ask__freeform" onSubmit={handleFreeformSubmit}>
          <input
            type="text"
            class="ion-ask__freeform-input"
            placeholder={
              multi
                ? 'Or type your own answer…'
                : 'Or type your own answer…'
            }
            value={freeform}
            onInput={(e) => setFreeform(e.target.value)}
            disabled={blocked}
          />
          <button
            type="submit"
            class="ion-ask__freeform-send"
            disabled={blocked || !freeform.trim()}
            aria-label="Send custom answer"
          >
            ↑
          </button>
        </form>
      )}
    </div>
  )
}
